import { memo, useCallback, useState, type FC } from "react";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import AudienceListSelect from "src/components/reusable/AudienceListSelect/AudienceListSelect";
import CampaignStepCard, { CampaignStepCardChromeContext } from "src/layouts/Engagements/Campaigns/shared/CampaignStepCard";
import CampaignFormField from "src/layouts/Engagements/Campaigns/shared/CampaignFormField";
import CampaignFlowActions from "src/layouts/Engagements/Campaigns/shared/CampaignFlowActions";
import CampaignNextButton from "src/layouts/Engagements/Campaigns/shared/CampaignNextButton";
import {
  ShowFieldErrorsContext,
  getFieldErrorId,
  useRequiredFieldError,
} from "src/layouts/Engagements/Campaigns/shared/UseRequiredFieldError";
import { EMAIL_WIZARD_STEPS, MAX_CAMPAIGN_NAME_LENGTH } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignConstants";
import { EMAIL_TEMPLATE_WIZARD_STEPS } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/Template/TemplateEditorConstants";
import { formatNumber } from "src/utils/numberFormat";
import { usePeopleReachedText } from "src/layouts/Engagements/Campaigns/shared/UsePeopleReachedText";
import SaveDraftModal from "src/layouts/Engagements/Campaigns/shared/SaveDraftModal";
import { useChatDraft } from "../UseChatDraft";
import AiGeneratorCard from "src/layouts/Engagements/Campaigns/CampaignSetup/AiGeneratorCard";
import ChannelSelector from "src/layouts/Engagements/Campaigns/CampaignSetup/ChannelSelector";
import type { useChatCampaignSetup } from "../UseChatCampaignSetup";
import "src/layouts/Engagements/Campaigns/CampaignSetup/CampaignSetup.scss";

const FIELD_IDS = {
  channelLabel: "chat-campaign-setup-channel-label",
  campaignName: "chat-campaign-setup-name",
  company: "chat-campaign-setup-company",
  sendTo: "chat-campaign-setup-send-to",
  dontSendTo: "chat-campaign-setup-dont-send-to",
};

const POPUP_CLASS = "campaign-flow__popup";

interface ChatCampaignSetupStepProps {
  setup: ReturnType<typeof useChatCampaignSetup>;
  onCancel: () => void;
  frozen?: boolean;
  isChannelLocked?: boolean;
  onDraftSaved?: () => void;
}

const ChatCampaignSetupStep: FC<ChatCampaignSetupStepProps> = ({
  setup,
  onCancel,
  frozen = false,
  isChannelLocked = false,
  onDraftSaved,
}) => {
  const { t, i18n } = useTranslation();
  const {
    channel,
    campaignName,
    companyName,
    sendTo,
    excludeList,
    audienceOptions,
    excludeOptions,
    isListsLoading,
    canSubmit,
    isAIGeneratorOn,
    isAIGeneratorDisabled,
    setChannel,
    setCampaignName,
    setSendTo,
    setExcludeList,
    handleAIGeneratorChange,
    handleSubmit,
  } = setup;

  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const revealFieldErrors = useCallback(() => setShowFieldErrors(true), []);
  const sendToField = useRequiredFieldError(sendTo, true, showFieldErrors);
  const nameField = useRequiredFieldError(campaignName, true, showFieldErrors);
  const isNameAtLimit = campaignName.length >= MAX_CAMPAIGN_NAME_LENGTH;
  const peopleReachedText = usePeopleReachedText(audienceOptions, sendTo);
  const { isDraftOpen, openDraft, closeDraft, handleSaveDraft } = useChatDraft(setup.saveDraft, onDraftSaved);
  const totalSteps = isAIGeneratorOn ? EMAIL_WIZARD_STEPS.length : EMAIL_TEMPLATE_WIZARD_STEPS.length;

  const stepLabel =
    channel === "email"
      ? t("wizardStepper.stepOf", {
          step: formatNumber(1, {}, i18n.language),
          total: formatNumber(totalSteps, {}, i18n.language),
        })
      : undefined;
  const chrome = {
    stepLabel,
    footer: (
      <CampaignFlowActions
        onBack={onCancel}
        backLabel={t("campaignFlow.common.cancel")}
        note={t("campaignFlow.aiWizard.chatNote")}
      >
        <button
          type="button"
          className="button button--link campaign-flow__action campaign-flow__action--link"
          onClick={openDraft}
        >
          {t("campaignFlow.common.saveDraft")}
        </button>
        <CampaignNextButton
          canProceed={canSubmit}
          onNext={handleSubmit}
          onBlockedClick={revealFieldErrors}
        >
          {t("campaignFlow.createCampaignNew.next")}
        </CampaignNextButton>
      </CampaignFlowActions>
    ),
  };

  return (
    <ShowFieldErrorsContext.Provider value={showFieldErrors}>
      <CampaignStepCardChromeContext.Provider value={chrome}>
      <CampaignStepCard
        title={t("campaignFlow.aiWizard.basics.title")}
        subtitle={t("campaignFlow.aiWizard.basics.chatSubtitle")}
      >
        {!isChannelLocked && (
        <CampaignFormField
          label={t("campaignFlow.createCampaignNew.channel")}
          labelId={FIELD_IDS.channelLabel}
          required
          hasError={showFieldErrors && !channel}
        >
          <ChannelSelector
            value={channel}
            onChange={setChannel}
            labelledBy={FIELD_IDS.channelLabel}
            onlySelected={isChannelLocked}
          />
        </CampaignFormField>
        )}

        <CampaignFormField
          label={t("campaignFlow.createCampaignNew.company")}
          htmlFor={FIELD_IDS.company}
          required
        >
          <StyledTextInput
            id={FIELD_IDS.company}
            value={companyName}
            disabled
            suffix={<Lock className="campaign-setup__lock-icon" aria-hidden />}
            className="campaign-flow__input"
          />
        </CampaignFormField>

        <CampaignFormField
          label={t("campaignFlow.common.campaignName")}
          htmlFor={FIELD_IDS.campaignName}
          required
          hasError={nameField.hasError || isNameAtLimit}
          errorMessage={isNameAtLimit ? t("campaignFlow.aiWizard.errors.campaignNameLimit") : undefined}
          counter={
            <span
              className={`campaign-flow-field__counter ${
                isNameAtLimit ? "campaign-flow-field__counter--error" : ""
              }`}
            >
              {formatNumber(campaignName.length, {}, i18n.language)}/
              {formatNumber(MAX_CAMPAIGN_NAME_LENGTH, {}, i18n.language)}
            </span>
          }
        >
          <StyledTextInput
            id={FIELD_IDS.campaignName}
            value={campaignName}
            maxLength={MAX_CAMPAIGN_NAME_LENGTH}
            onChange={event => setCampaignName(event.target.value)}
            onBlur={nameField.handleBlur}
            placeholder={t("campaignFlow.createCampaignNew.campaignNamePlaceholder")}
            autoComplete="off"
            aria-invalid={nameField.hasError || isNameAtLimit}
            aria-describedby={
              nameField.hasError || isNameAtLimit ? getFieldErrorId(FIELD_IDS.campaignName) : undefined
            }
            className="campaign-flow__input"
          />
        </CampaignFormField>

        <div className="campaign-flow__field-row">
          <CampaignFormField
            label={t("campaignFlow.createCampaignNew.sendTo")}
            htmlFor={FIELD_IDS.sendTo}
            required
            hasError={sendToField.hasError}
            helperText={peopleReachedText}
          >
            <AudienceListSelect
              id={FIELD_IDS.sendTo}
              value={sendTo}
              options={audienceOptions}
              onChange={setSendTo}
              onBlur={sendToField.handleBlur}
              aria-invalid={sendToField.hasError}
              aria-describedby={
                sendToField.hasError ? getFieldErrorId(FIELD_IDS.sendTo) : undefined
              }
              loading={isListsLoading}
              placeholder={t("campaignFlow.createCampaignNew.selectAudience")}
              popupClassName={POPUP_CLASS}
              showSelectedIcon
            />
          </CampaignFormField>

          <CampaignFormField
            label={t("campaignFlow.createCampaignNew.dontSendTo")}
            htmlFor={FIELD_IDS.dontSendTo}
            hint={t("campaignFlow.createCampaignNew.optionalHint")}
            helperText={t("campaignFlow.aiWizard.basics.exclusionHint")}
          >
            <AudienceListSelect
              id={FIELD_IDS.dontSendTo}
              value={excludeList}
              options={excludeOptions}
              onChange={setExcludeList}
              loading={isListsLoading}
              placeholder={t("campaignFlow.createCampaignNew.selectExclusions")}
              popupClassName={POPUP_CLASS}
              showSelectedIcon
            />
          </CampaignFormField>
        </div>

        {channel === "email" && (
          <AiGeneratorCard
            checked={isAIGeneratorOn}
            disabled={isAIGeneratorDisabled}
            onChange={handleAIGeneratorChange}
          />
        )}
      </CampaignStepCard>
      </CampaignStepCardChromeContext.Provider>
      {!frozen && (
        <SaveDraftModal
          open={isDraftOpen}
          isSaving={setup.isSavingDraft}
          onKeepEditing={closeDraft}
          onSaveAndExit={handleSaveDraft}
        />
      )}
    </ShowFieldErrorsContext.Provider>
  );
};

export default memo(ChatCampaignSetupStep);
